/* eslint-disable @typescript-eslint/no-explicit-any */
// SafariMemoryManager.ts
export interface BrowserCapabilities {
  isSafari: boolean
  isIOS: boolean
  maxCanvasMemory: number
  maxCanvasArea: number
  supportsOffscreenCanvas: boolean
  devicePixelRatio: number
}

export interface CanvasPoolItem {
  canvas: HTMLCanvasElement
  context: CanvasRenderingContext2D | null
  width: number
  height: number
  lastUsed: number
  pageNumber?: number
}

export class SafariMemoryManager {
  private static instance: SafariMemoryManager
  private canvasPool = new Map<string, CanvasPoolItem>()
  private memoryUsage = 0
  private readonly capabilities: BrowserCapabilities
  private cleanupTimer: NodeJS.Timeout | null = null
  private readonly CLEANUP_INTERVAL = 5000 // 5 seconds
  private readonly MAX_POOL_SIZE = 10
  private readonly MEMORY_WARNING_THRESHOLD = 0.8 // 80% of max memory

  constructor() {
    this.capabilities = this.detectBrowserCapabilities()
    this.startPeriodicCleanup()
  }

  static getInstance(): SafariMemoryManager {
    if (!SafariMemoryManager.instance) {
      SafariMemoryManager.instance = new SafariMemoryManager()
    }
    return SafariMemoryManager.instance
  }

  private detectBrowserCapabilities(): BrowserCapabilities {
    const userAgent = navigator.userAgent
    const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent)
    const isIOS = /iPad|iPhone|iPod/.test(userAgent)

    return {
      isSafari,
      isIOS,
      maxCanvasMemory: this.getMaxCanvasMemory(isSafari, isIOS),
      maxCanvasArea: isSafari ? 16777216 : 268435456, // 4096x4096 vs 16384x16384
      supportsOffscreenCanvas: 'OffscreenCanvas' in window,
      devicePixelRatio: Math.min(2, window.devicePixelRatio || 1),
    }
  }

  private getMaxCanvasMemory(isSafari: boolean, isIOS: boolean): number {
    if (isIOS) return 256 * 1024 * 1024 // 256MB for iOS
    if (isSafari) return 384 * 1024 * 1024 // 384MB for Safari
    return 1024 * 1024 * 1024 // 1GB for other browsers
  }

  private generateCanvasKey(width: number, height: number): string {
    return `${width}x${height}`
  }

  private estimateCanvasMemory(width: number, height: number, pixelRatio: number = 1): number {
    // 4 bytes per pixel (RGBA) * pixel ratio squared
    return width * height * pixelRatio * pixelRatio * 4
  }

  private cleanupCanvas(item: CanvasPoolItem): void {
    const { canvas, context } = item

    try {
      // Clear the canvas completely
      if (context) {
        context.clearRect(0, 0, canvas.width, canvas.height)
        // Force context cleanup on Safari
        if (this.capabilities.isSafari) {
          context.fillStyle = '#000000'
          context.fillRect(0, 0, 1, 1)
          context.clearRect(0, 0, 1, 1)
        }
      }

      // Reset canvas dimensions to free memory
      canvas.width = 1
      canvas.height = 1

      // Remove from DOM if attached
      if (canvas.parentNode) {
        canvas.parentNode.removeChild(canvas)
      }
    } catch (error) {
      // Silent cleanup - errors are expected during memory pressure
    }
  }

  private startPeriodicCleanup(): void {
    this.cleanupTimer = setInterval(() => {
      this.performCleanup()
    }, this.CLEANUP_INTERVAL)
  }

  private performCleanup(): void {
    const now = Date.now()
    const staleThreshold = 30000 // 30 seconds
    const itemsToCleanup: string[] = []

    // Find stale canvases
    this.canvasPool.forEach((item, key) => {
      if (now - item.lastUsed > staleThreshold) {
        itemsToCleanup.push(key)
      }
    })

    // Cleanup stale canvases
    itemsToCleanup.forEach((key) => {
      const item = this.canvasPool.get(key)
      if (item) {
        this.cleanupCanvas(item)
        this.memoryUsage -= this.estimateCanvasMemory(item.width, item.height)
        this.canvasPool.delete(key)
      }
    })

    // If memory usage is high, cleanup more aggressively
    if (this.isMemoryPressureHigh()) {
      this.performAggressiveCleanup()
    }

    // Force garbage collection if available (mostly for development)
    if ((window as any).gc && this.capabilities.isSafari) {
      try {
        ;(window as any).gc()
      } catch (e) {
        // Ignore if gc is not available
      }
    }
  }

  private isMemoryPressureHigh(): boolean {
    return this.memoryUsage > this.capabilities.maxCanvasMemory * this.MEMORY_WARNING_THRESHOLD
  }

  private performAggressiveCleanup(): void {
    // Sort by last used time (oldest first)
    const sortedItems = Array.from(this.canvasPool.entries()).sort(
      ([, a], [, b]) => a.lastUsed - b.lastUsed
    )

    // Remove half of the canvases to free memory
    const itemsToRemove = Math.ceil(sortedItems.length / 2)

    for (let i = 0; i < itemsToRemove && i < sortedItems.length; i++) {
      const [key, item] = sortedItems[i]
      this.cleanupCanvas(item)
      this.memoryUsage -= this.estimateCanvasMemory(item.width, item.height)
      this.canvasPool.delete(key)
    }
  }

  canAllocateCanvas(width: number, height: number, pixelRatio: number = 1): boolean {
    const requiredMemory = this.estimateCanvasMemory(width, height, pixelRatio)
    const totalMemoryAfter = this.memoryUsage + requiredMemory

    // Check area limit
    if (width * height > this.capabilities.maxCanvasArea) {
      return false
    }

    // Check memory limit
    return totalMemoryAfter <= this.capabilities.maxCanvasMemory * 0.9 // 90% safety margin
  }

  getOptimalCanvasSize(
    requestedWidth: number,
    requestedHeight: number
  ): {
    width: number
    height: number
    scale: number
  } {
    const maxArea = this.capabilities.maxCanvasArea
    const requestedArea = requestedWidth * requestedHeight

    if (requestedArea <= maxArea) {
      return {
        width: requestedWidth,
        height: requestedHeight,
        scale: 1,
      }
    }

    // Calculate scale to fit within area limit
    const scale = Math.sqrt(maxArea / requestedArea) * 0.95 // 5% safety margin

    return {
      width: Math.floor(requestedWidth * scale),
      height: Math.floor(requestedHeight * scale),
      scale,
    }
  }

  getOptimalRenderSettings(
    requestedWidth: number,
    requestedHeight: number
  ): {
    width: number
    height: number
    scale: number
    devicePixelRatio: number
  } {
    const optimalSize = this.getOptimalCanvasSize(requestedWidth, requestedHeight)

    return {
      width: optimalSize.width,
      height: optimalSize.height,
      scale: optimalSize.scale,
      devicePixelRatio: this.capabilities.devicePixelRatio,
    }
  }

  getCanvas(width: number, height: number, pageNumber?: number): HTMLCanvasElement | null {
    const key = this.generateCanvasKey(width, height)
    const now = Date.now()

    // Check if we can allocate this canvas size
    if (!this.canAllocateCanvas(width, height, this.capabilities.devicePixelRatio)) {
      return null
    }

    // Try to reuse existing canvas
    let poolItem = this.canvasPool.get(key)

    if (poolItem) {
      poolItem.lastUsed = now
      poolItem.pageNumber = pageNumber
      return poolItem.canvas
    }

    // Check pool size limit
    if (this.canvasPool.size >= this.MAX_POOL_SIZE) {
      this.performCleanup()
    }

    // Create new canvas
    try {
      const canvas = document.createElement('canvas')
      const context = canvas.getContext('2d', {
        alpha: false, // Disable alpha for better performance
        desynchronized: true, // Better performance on some browsers
      })

      if (!context) {
        return null
      }

      canvas.width = width
      canvas.height = height

      // Safari-specific optimizations
      if (this.capabilities.isSafari) {
        canvas.style.imageRendering = 'high-quality'
        // Prevent canvas from being too large
        canvas.style.maxWidth = '100%'
        canvas.style.height = 'auto'
      }

      poolItem = {
        canvas,
        context,
        width,
        height,
        lastUsed: now,
        pageNumber,
      }

      this.canvasPool.set(key, poolItem)
      this.memoryUsage += this.estimateCanvasMemory(
        width,
        height,
        this.capabilities.devicePixelRatio
      )

      return canvas
    } catch (error) {
      return null
    }
  }

  releaseCanvas(pageNumber: number): void {
    // Find and release canvases for specific page
    this.canvasPool.forEach((item, key) => {
      if (item.pageNumber === pageNumber) {
        this.cleanupCanvas(item)
        this.memoryUsage -= this.estimateCanvasMemory(item.width, item.height)
        this.canvasPool.delete(key)
      }
    })
  }

  getMemoryUsage(): {
    used: number
    max: number
    percentage: number
    canvasCount: number
  } {
    return {
      used: this.memoryUsage,
      max: this.capabilities.maxCanvasMemory,
      percentage: (this.memoryUsage / this.capabilities.maxCanvasMemory) * 100,
      canvasCount: this.canvasPool.size,
    }
  }

  getBrowserCapabilities(): BrowserCapabilities {
    return { ...this.capabilities }
  }

  destroy(): void {
    // Cleanup all canvases
    this.canvasPool.forEach((item) => {
      this.cleanupCanvas(item)
    })
    this.canvasPool.clear()
    this.memoryUsage = 0

    // Clear cleanup timer
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer)
      this.cleanupTimer = null
    }
  }
}

// Singleton instance
export const safariMemoryManager = SafariMemoryManager.getInstance()
